import { Injectable } from "@angular/core";
import { getLayoutTypeName, IDashboardWidgetView, LayoutType } from "@main/reporting.v3/dashboard/dashboard-models";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { WidgetOutputMode } from "@main/reporting.v3/core/reporting-models";

@Injectable()
export class DashboardAuxiliaryService {
    public readonly widgetClass: string = "dashboard-widget";
    public readonly widgetRowClass: string = "dashboard-widget-row";
    public readonly widgetGridClass: string = "dashboard-widget-grid";
    public readonly widgetSeparatorClass: string = "dashboard-widget-separator";
    public readonly widgetRowSeparatorClass: string = "dashboard-widget-row-separator";

    public readonly dashboardHeaderClass: string = "dashboard-header";
    public readonly dashboardContainerClass: string = "dashboard-container";
    public readonly dashboardFooterClass: string = "dashboard-footer";

    public readonly dashboardHeaderDefaultStylesClass: string = "dashboard-header-default-styles";
    public readonly dashboardContainerDefaultStylesClass: string = "dashboard-container-default-styles";
    public readonly dashboardFooterDefaultStylesClass: string = "dashboard-footer-default-styles";

    public readonly htmlCodeContainerClass: string = "html-code-container";
    public readonly widgetElementTag: string = "reporting-widget";

    constructor(private readonly htmlRenderingService: HtmlRenderingService) {
    }

    public getDashboardDefaultStyles(): string {
        return this.htmlRenderingService.getDefaultCssRules(`.${this.dashboardContainerDefaultStylesClass}`);
    }

    public getDashboardHeaderDefaultHtml(): string {
        return "<span></span>";
    }

    public getDashboardHeaderDefaultStyles(): string {
        return this.htmlRenderingService.getDefaultCssRules(`.${this.dashboardHeaderDefaultStylesClass}`);
    }

    public getDashboardHeaderRendererStyles(): string {
        return this.htmlRenderingService.getReplacedCssRules(`.${this.htmlCodeContainerClass}`, `.${this.dashboardHeaderClass}`);
    }

    public getDashboardFooterDefaultHtml(): string {
        return "Page <span class='pageNumber'>0</span> of <span class='totalPages'>0</span>";
    }

    public getDashboardFooterDefaultStyles(): string {
        return this.htmlRenderingService.getDefaultCssRules(`.${this.dashboardFooterDefaultStylesClass}`);
    }

    public getDashboardFooterRendererStyles(): string {
        return this.htmlRenderingService.getReplacedCssRules(`.${this.htmlCodeContainerClass}`, `.${this.dashboardFooterClass}`);
    }

    public getWidgetGridContainerClass(layout: LayoutType): string {
        const layoutClass: string = getLayoutTypeName(layout).toLowerCase();
        return `${this.widgetGridClass} ${layoutClass}`;
    }

    public getWidgetContainerClass(widget: IDashboardWidgetView, components: IWidgetComponent[][], output: WidgetOutputMode): string {
        if (!widget || !components?.length) return this.widgetClass;

        const row: IWidgetComponent[] = components[widget.rowIndex];
        if (!row?.length || row.length <= widget.columnIndex) return this.widgetClass;

        return this.widgetVisible(row[widget.columnIndex], output) ? this.widgetClass : `${this.widgetClass} hidden`;
    }

    public getWidgetContainerStyle(widget: IDashboardWidgetView, components: IWidgetComponent[][], output: WidgetOutputMode, margin: number): string {
        if (!widget || !components?.length) return "";

        const row: IWidgetComponent[] = components[widget.rowIndex];
        const count: number = row?.filter(widget => this.widgetVisible(widget, output))?.length;

        return !!count ? `width: calc(${100 / count}% - ${margin * 2}px);` : "";
    }

    public getSeparatorClass(components: IWidgetComponent[][], output: WidgetOutputMode, row: number, column?: number): string {
        const separatorClass: string = column == null ? this.widgetRowSeparatorClass : this.widgetSeparatorClass;

        if (!components?.length) return separatorClass;

        if (column == null && row > 0) {
            const visible: boolean = components[row - 1].some(component => this.widgetVisible(component, output));
            if (!visible) return `${separatorClass} hidden`;

        } else if (column > 0) {
            const visible: boolean = this.widgetVisible(components[row][column - 1], output);
            if (!visible) return `${separatorClass} hidden`;
        }

        return separatorClass;
    }

    private widgetVisible(component: IWidgetComponent, output: WidgetOutputMode): boolean {
        return output !== "PDF" ? !!component?.widgetVisible : !!(component as unknown as IPdfExportable)?.pdfViewVisible;
    }
}